const DARK_TEXT = '#1F2937';

/** Próg WCAG AA dla zwykłego tekstu. */
const MIN_CONTRAST_ON_WHITE = 4.5;
const DARKEN_FACTOR = 0.9;
const MAX_DARKEN_STEPS = 40;

/** Paleta barw nadawanych tagom, gdy nikt nie wybrał koloru ręcznie. */
export const TAG_PALETTE = [
  '#2563EB',
  '#DC2626',
  '#F59E0B',
  '#16A34A',
  '#9333EA',
  '#0891B2',
  '#DB2777',
  '#65A30D',
  '#EA580C',
  '#475569',
];

/** Barwa pierwszego tagu w formularzu i zapas dla pola koloru. */
export const DEFAULT_TAG_COLOR = TAG_PALETTE[0];

/** Losowa barwa z palety — domyślna dla nowo zakładanego tagu. */
export function getRandomTagColor(): string {
  return TAG_PALETTE[Math.floor(Math.random() * TAG_PALETTE.length)];
}

type Rgb = { r: number; g: number; b: number };

function parseHex(hex: string): Rgb | null {
  const normalized = hex.replace('#', '').trim();
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** Luminancja względna wg WCAG 2.1 — składowe sRGB linearyzowane przed ważeniem. */
function relativeLuminance({ r, g, b }: Rgb): number {
  const linear = (value: number) => {
    const c = Math.min(255, Math.max(0, value)) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

function contrastWithWhite(rgb: Rgb): number {
  return 1.05 / (relativeLuminance(rgb) + 0.05);
}

/**
 * Przyciemnia kolor tagu, aż napis w tej barwie przejdzie AA na bieli.
 *
 * Kolor tagu jest edytowalny przez użytkownika, a i domyślna paleta ma odcienie
 * lądujące przy 2 : 1 na bieli (#F59E0B) — obrysowany chip nie może im ufać.
 * Skalowanie wszystkich składowych tym samym współczynnikiem zachowuje odcień.
 */
export function getReadableTextColorOnWhite(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return DARK_TEXT;

  let current = rgb;
  let steps = 0;
  while (contrastWithWhite(current) < MIN_CONTRAST_ON_WHITE) {
    if (steps >= MAX_DARKEN_STEPS) return DARK_TEXT;
    current = {
      r: current.r * DARKEN_FACTOR,
      g: current.g * DARKEN_FACTOR,
      b: current.b * DARKEN_FACTOR,
    };
    steps += 1;
  }

  return steps === 0 ? hex : toHex(current);
}
